import {FormError, FormValues} from '@wix/form-viewer'
import {FormViewer, FormViewerContext, FormViewerHandle} from '@wix/form-viewer/widget'
import {getFormMessages, isRsvp, isTicketed, isWaitlistOpen} from '@wix/wix-events-commons-statics'
import {useBi, useTranslation} from '@wix/yoshi-flow-editor'
import React, {useEffect, useRef, useState} from 'react'
import {useAppActions, useAppProps, useAppSelector} from '../../hooks/runtime'
import {getUseBuyerDetails} from '../../selectors/checkout'
import {isMember} from '../../selectors/current-member-details'
import {getEvent} from '../../selectors/event'
import {hasPolicies as hasPoliciesSelector} from '../../selectors/policies'
import {FormSubmitButton} from '../form-submit-button'
import {FormViewerContextProps, WixFormProps} from './interfaces'
import {buildOverrides, getSubmitButtonText} from './utils'
import s from './wix-form.scss'

export const WixForm = ({formId, formIndex, ticketForm, lastTicket, initialValues = {}, onSubmit}: WixFormProps) => {
  const bi = useBi()
  const {i18n, t} = useTranslation()
  const appProps = useAppProps() as FormViewerContextProps
  const {setTicketDetails} = useAppActions()

  const event = useAppSelector(getEvent)
  const member = useAppSelector(isMember)
  const hasPolicies = useAppSelector(hasPoliciesSelector)
  const useBuyerDetails = useAppSelector(getUseBuyerDetails)

  const formViewer = useRef<FormViewerHandle>(null)
  const [formValues, setFormValues] = useState<FormValues>(initialValues)
  const [formErrors, setFormErrors] = useState<FormError[]>([])

  const rsvp = isRsvp(event)
  const ticketed = isTicketed(event)
  const waitlistOpen = isWaitlistOpen(event)
  const messages = getFormMessages(event)
  const overrides = buildOverrides(initialValues, member, rsvp, ticketed, useBuyerDetails)
  const submitButtonText = getSubmitButtonText(messages, ticketed, hasPolicies, waitlistOpen, t)

  const submitForm = async () => {
    const valid = await formViewer.current.validate()

    if (valid) {
      onSubmit(formValues)
    }
  }

  const handleChange = (values: FormValues) => {
    setFormValues(values)

    if (ticketForm) {
      setTicketDetails({index: formIndex, details: values, valid: true})
    }
  }

  useEffect(() => {
    setFormValues(initialValues)
  }, [initialValues])

  return (
    <FormViewerContext {...appProps}>
      <div className={s.container}>
        <FormViewer
          i18n={i18n}
          bi={bi}
          ref={formViewer}
          values={formValues}
          errors={formErrors}
          formId={formId}
          overrides={overrides}
          onValidate={setFormErrors}
          onChange={handleChange}
        />
        {!ticketForm && <FormSubmitButton text={submitButtonText} onClick={submitForm} />}
        {ticketForm && !lastTicket && (
          <button className={s.formButton} type="submit" onClick={submitForm}>
            {t('mobile.checkout.ticket.nextTicket')}
          </button>
        )}
      </div>
    </FormViewerContext>
  )
}
